// @admin-async-case-v1 (20 Oct 2026)
// Read-only copy of the case screen the doctor works in (doctor portal
// pages/SickLeave.jsx and pages/Renewals.jsx), for the admin consultation page.
// Same sections, same order, same wording; no buttons, nothing can be changed.
import React from 'react';

const NAVY = 'var(--navy)';
const MUTED = 'var(--muted)';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('el-GR') : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('el-GR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const GENDER = (g) => {
  const s = String(g || '').toLowerCase();
  if (s.startsWith('f') || s.startsWith('γ')) return 'Γυναίκα';
  if (s.startsWith('m') || s.startsWith('α')) return 'Άνδρας';
  return '—';
};

const DOC_LABEL = { passport: 'Διαβατήριο', national_id: 'Ταυτότητα', driving_licence: 'Δίπλωμα' };

const SL_STATUS = {
  awaiting_payment: 'Αναμονή πληρωμής', open: 'Στην ουρά', claimed: 'Σε αξιολόγηση',
  info_requested: 'Αναμονή απάντησης ασθενή', completed: 'Ολοκληρώθηκε',
  expired: 'Έληξε (χωρίς γιατρό)', closed_no_reply: 'Έκλεισε (χωρίς απάντηση ασθενή)',
};
const SL_DECISION = { issued: 'Έκδοση βεβαίωσης', declined: 'Άρνηση', referred: 'Παραπομπή' };

const RN_STATUS = {
  awaiting_payment: 'Αναμονή πληρωμής', open: 'Στην ουρά', claimed: 'Σε αξιολόγηση',
  info_requested: 'Αναμονή απάντησης ασθενή', completed: 'Ολοκληρώθηκε', expired: 'Έληξε',
};
const RN_REASON = {
  needs_consultation: 'Χρειάζεται συζήτηση (βιντεοκλήση)',
  needs_tests: 'Χρειάζονται πρόσφατες εξετάσεις',
  see_own_doctor: 'Επίσκεψη στον θεράποντα γιατρό',
  not_suitable_service: 'Δεν εξυπηρετείται από την υπηρεσία',
  other: 'Άλλο',
};

const labelFrom = (list, code) => {
  const hit = (list || []).find((r) => (typeof r === 'string' ? r : r.code) === code);
  if (!hit) return code || '—';
  return typeof hit === 'string' ? hit : (hit.label_el || hit.label || hit.code);
};

const Box = ({ title, right, children }) => (
  <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
    {(title || right) && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: NAVY }}>{title}</h3>
        {right}
      </div>
    )}
    {children}
  </div>
);

const Pill = ({ children, tone = 'grey' }) => {
  const c = {
    grey: ['#F1F3F6', '#4A5568'], green: ['#E7F7EF', '#0D7A52'], red: ['#FDECEC', '#B42318'], amber: ['#FFF4E5', '#B45309'],
  }[tone];
  return <span style={{ background: c[0], color: c[1], borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{children}</span>;
};

const Facts = ({ rows }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
    {rows.map(([k, v]) => (
      <div key={k} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 12px' }}>
        <div style={{ fontSize: 12, color: MUTED }}>{k}</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginTop: 2 }}>{v}</div>
      </div>
    ))}
  </div>
);

const Bubble = ({ fromDoctor, who, at, body }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: fromDoctor ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
    <span style={{ fontSize: 12, color: MUTED, marginBottom: 2 }}>{who}{at ? ` · ${fmtDateTime(at)}` : ''}</span>
    <div style={{ maxWidth: '80%', whiteSpace: 'pre-wrap', fontSize: 14, color: NAVY, padding: '8px 12px', borderRadius: 10,
      background: fromDoctor ? '#E8F0FE' : 'var(--bg)' }}>{body}</div>
  </div>
);

const Banner = () => (
  <div style={{ background: '#EEF2F7', borderLeft: '4px solid var(--navy)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: NAVY }}>
    Η οθόνη του γιατρού, μόνο για ανάγνωση.
  </div>
);

function SickLeaveCase({ d }) {
  const req = d.request || {};
  const patient = d.patient || {};
  const answers = req.answers || {};
  const flags = req.flags || {};
  const tripped = Array.isArray(flags.tripped) ? flags.tripped : [];
  const idLine = patient.amka
    ? `ΑΜΚΑ ${patient.amka}`
    : patient.id_document_number
      ? [DOC_LABEL[patient.id_document_type] || 'Έγγραφο', patient.id_document_country, patient.id_document_number].filter(Boolean).join(' ')
      : 'Χωρίς ΑΜΚΑ ή έγγραφο';
  const decided = req.status === 'completed' && req.decision;
  const tone = req.decision === 'issued' ? 'green' : req.decision ? 'red' : 'grey';

  return (
    <>
      <Banner />
      <Box title={`📝 Αναρρωτική άδεια — ${patient.first_name || ''} ${patient.last_name || ''}`.trim()}
        right={<Pill tone={req.status === 'completed' ? tone : 'grey'}>{SL_STATUS[req.status] || req.status}</Pill>}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <Pill>{idLine}</Pill>
          <Pill>γεν. {fmtDate(patient.date_of_birth)}</Pill>
        </div>
        {tripped.length > 0 && (
          <div style={{ background: '#FDECEC', color: '#B42318', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 14 }}>
            ⚠️ Θετικές απαντήσεις ασφαλείας: {tripped.join(', ')}
          </div>
        )}
        <Facts rows={[
          ['Έναρξη απουσίας', fmtDate(req.absence_start)],
          ['Ζήτησε', req.requested_days ? `${req.requested_days} ημέρες` : '—'],
          ['Πληρωμή', fmtDate(req.paid_at)],
        ]} />
      </Box>

      <Box title="Συμπτώματα, όπως τα έγραψε">
        <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 14, color: NAVY }}>{req.symptoms || '—'}</p>
      </Box>

      <Box title="Απαντήσεις">
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: NAVY, lineHeight: 1.8 }}>
          {Object.keys(answers).length === 0 && <li style={{ color: MUTED }}>—</li>}
          {Object.entries(answers).map(([k, v]) => (
            <li key={k}>{k}: <strong>{v === true ? 'Ναι' : v === false ? 'Όχι' : String(v)}</strong></li>
          ))}
        </ul>
      </Box>

      <Box title="Συνομιλία">
        {(d.messages || []).length === 0 && <p style={{ margin: 0, color: MUTED, fontSize: 14 }}>Κανένα μήνυμα.</p>}
        {(d.messages || []).map((m, i) => (
          <Bubble key={i} fromDoctor={m.sender_role === 'doctor'}
            who={m.sender_name || (m.sender_role === 'doctor' ? 'Γιατρός' : 'Ασθενής')} at={m.created_at} body={m.body} />
        ))}
      </Box>

      <Box title="Απόφαση">
        {!decided && <p style={{ margin: 0, color: MUTED, fontSize: 14 }}>Δεν έχει καταχωρηθεί απόφαση.</p>}
        {decided && (
          <Facts rows={[
            ['Απόφαση', SL_DECISION[req.decision] || req.decision],
            ...(req.decision === 'issued' ? [
              ['Ημέρες', req.issued_days ? String(req.issued_days) : '—'],
              ['Κάλυψη', `${fmtDate(req.issued_start)} έως ${fmtDate(req.issued_end)}`],
            ] : []),
            ...(req.decision === 'declined' ? [['Λόγος άρνησης', labelFrom(d.config?.declineReasons, req.decline_reason_code)]] : []),
            ...(req.decision === 'referred' ? [['Παραπομπή σε', labelFrom(d.config?.referTargets, req.refer_target)]] : []),
            ['Ολοκληρώθηκε', fmtDateTime(req.completed_at)],
          ]} />
        )}
        {req.doctor_note && (
          <div style={{ marginTop: 12, fontSize: 14, color: NAVY }}>
            <span style={{ color: MUTED }}>Σημείωση για τον φάκελο: </span>{req.doctor_note}
          </div>
        )}
      </Box>
    </>
  );
}

function RenewalCase({ d }) {
  const a = d.answers || {};
  const items = d.items || [];
  const approved = items.filter((it) => it.decision === 'approved').length;
  const outcomeTone = d.status !== 'completed' ? 'grey' : approved === 0 ? 'red' : approved < items.length ? 'amber' : 'green';
  const outcomeLabel = d.status !== 'completed' ? (RN_STATUS[d.status] || d.status)
    : approved === 0 ? 'Απορρίφθηκε' : approved < items.length ? 'Μερική έγκριση' : 'Εγκρίθηκε';

  return (
    <>
      <Banner />
      <Box title={`💊 Επανάληψη συνταγής — ${d.patient?.firstName || ''} ${d.patient?.lastName || ''}`.trim()}
        right={<Pill tone={outcomeTone}>{outcomeLabel}</Pill>}>
        <div style={{ color: MUTED, fontSize: 14, marginBottom: 14 }}>
          {d.patient?.age} ετών · {GENDER(d.patient?.gender)} · ΑΜΚΑ {d.patient?.amka || '—'}
        </div>
        {d.flags?.length > 0 && (
          <div style={{ background: '#FFF4E5', color: '#B45309', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 14 }}>
            {d.flags.map((f) => (
              <div key={f.id}><b>⚑ {f.note}</b>{f.answer && f.answer !== '—' ? <> — «{f.answer}»</> : null}</div>
            ))}
          </div>
        )}
        <Facts rows={[
          ['Αλλεργίες', a.noKnownAllergies ? 'Καμία' : (a.allergies || []).map((x) => x.allergen).join(', ') || '—'],
          ['Χρόνια νοσήματα', a.noChronicConditions ? 'Κανένα' : (a.conditions || []).map((c) => c.condition_name).join(', ') || '—'],
          ['Πίεση / σφύξεις', a.noVitals ? 'Χωρίς μέτρηση' : `${a.systolic ?? '—'}/${a.diastolic ?? '—'} · ${a.pulse ?? '—'}`],
          ['Κάτι νέο;', a.anythingNew ? (a.anythingNewText || 'Ναι') : 'Όχι'],
          ...(a.pregnantOrBreastfeeding === false ? [['Κύηση / θηλασμός', 'Όχι']] : []),
          ['Επαναλήψεις 12μήνου', `${d.history?.usedThisPeriod ?? '—'} από ${d.history?.maxPerYear ?? '—'}`],
          ['Τελ. βιντεοκλήση DocNow', d.history?.lastVideoConsultationAt ? fmtDateTime(d.history.lastVideoConsultationAt) : '—'],
          ['Πληρωμή', fmtDateTime(d.paidAt)],
        ]} />
      </Box>

      <Box title={`Φάρμακα (${items.length})`}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: MUTED, fontSize: 12 }}>
              <th style={{ padding: '6px 8px' }}>Φάρμακο</th><th style={{ padding: '6px 8px' }}>Δόση</th><th style={{ padding: '6px 8px' }}>Απόφαση</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ borderTop: '1px solid #E8ECF2',
                background: it.decision === 'rejected' ? '#FFF7F7' : it.decision === 'approved' ? '#F5FCF8' : 'transparent' }}>
                <td style={{ padding: '8px' }}>
                  <b style={{ color: NAVY }}>{it.name}</b>
                  <div style={{ color: MUTED, fontSize: 12 }}>
                    {it.isFreeText ? '⚠ εκτός λίστας' : [it.substance, it.atc].filter(Boolean).join(' · ')}
                    {it.forType2Diabetes && ' · για ΣΔ τύπου 2'}
                  </div>
                </td>
                <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{it.unitsPerDose} × {it.timesPerDay}/ημ.</td>
                <td style={{ padding: '8px' }}>
                  {it.decision === 'approved' && <Pill tone="green">✓ Έγκριση</Pill>}
                  {it.decision === 'rejected' && (
                    <>
                      <Pill tone="red">✗ Απόρριψη</Pill>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
                        {RN_REASON[it.rejectReasonCode] || it.rejectReasonCode}{it.rejectReasonText ? ` — ${it.rejectReasonText}` : ''}
                      </div>
                    </>
                  )}
                  {!it.decision && <span style={{ color: MUTED }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {d.profileMedications?.length > 0 && (
        <Box title={`Φάρμακα στο προφίλ ασθενή (${d.profileMedications.length})`}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: NAVY, lineHeight: 1.8 }}>
            {d.profileMedications.map((m, i) => (
              <li key={i}>{m.medication_name}{m.dosage ? ` · ${m.dosage}` : ''}{m.frequency ? ` · ${m.frequency}` : ''}</li>
            ))}
          </ul>
        </Box>
      )}

      <Box title="Μηνύματα">
        {(d.messages || []).length === 0 && <p style={{ margin: 0, color: MUTED, fontSize: 14 }}>Κανένα μήνυμα.</p>}
        {(d.messages || []).map((m, i) => (
          <Bubble key={i} fromDoctor={m.from === 'doctor'} who={m.from === 'doctor' ? 'Γιατρός' : 'Ασθενής'} at={m.at} body={m.body} />
        ))}
      </Box>

      <Box title="Ολοκλήρωση">
        <Facts rows={[
          ['Κατάσταση', RN_STATUS[d.status] || d.status],
          ['Ολοκληρώθηκε', fmtDateTime(d.completedAt)],
          ['Εκτέλεση < 28 ημέρες', d.dispensedUnder28Days ? 'Ναι' : 'Όχι'],
          ['Επιστροφή χρημάτων', d.refundStatus === 'refunded' ? 'Ναι' : d.refundStatus === 'pending' ? 'Σε εξέλιξη' : 'Όχι'],
        ]} />
        {d.doctorNote && (
          <div style={{ marginTop: 12, fontSize: 14, color: NAVY }}>
            <span style={{ color: MUTED }}>Σημείωση προς τον ασθενή: </span>{d.doctorNote}
          </div>
        )}
      </Box>
    </>
  );
}

export default function AsyncCaseView({ caseData }) {
  if (!caseData || !caseData.data) return null;
  return caseData.kind === 'sick_leave'
    ? <SickLeaveCase d={caseData.data} />
    : <RenewalCase d={caseData.data} />;
}
